import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Users, FileText, GraduationCap, BarChart3, X } from "lucide-react";

const STEPS = [
  {
    icon: Users,
    title: "Your client CRM",
    body: "Every seeker who books with you gets a private dossier — notes, tags, and full booking history. Only you see what you write.",
    cta: "See CRM",
    to: "/pro/clients",
  },
  {
    icon: FileText,
    title: "Auto-issued invoices",
    body: "Every paid booking generates a numbered PDF invoice. Cmd+P to print or send to clients as tax-ready proof.",
    cta: "See invoices",
    to: "/pro/invoices",
  },
  {
    icon: GraduationCap,
    title: "Sell courses & memberships",
    body: "Bundle your knowledge into paid courses with lessons, videos, and completion certificates. Recurring revenue between bookings.",
    cta: "Create a course",
    to: "/pro/courses",
  },
  {
    icon: BarChart3,
    title: "See what's working",
    body: "Advanced analytics reveal your best months, most loyal clients, and top services. Grow with signal — not guesswork.",
    cta: "Open analytics",
    to: "/pro/analytics",
  },
];

const STORAGE_KEY = "cauldron_pro_tour_shown_v1";

/**
 * OnboardingTour — mounts once, checks whether the user is Pro + hasn't
 * already seen the tour, and shows a 4-step overlay welcoming them to the
 * Pro toolkit. Dismiss OR "Skip" writes the localStorage flag so it never
 * re-appears.
 */
export default function OnboardingTour() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const location = useLocation();

  // Thin helper — never await since we don't want to block UI if tracking fails.
  const track = (action, stepIdx = null) => {
    try {
      const token = localStorage.getItem("cauldron_token");
      if (!token) return;
      const API = process.env.REACT_APP_BACKEND_URL + "/api";
      fetch(`${API}/onboarding/track`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tour: "pro_v1",
          step: stepIdx == null ? step : stepIdx,
          action,
        }),
      }).catch(() => {});
    } catch {}
  };

  useEffect(() => {
    // Scope: only mount on Pro-tool paths — never block admin or marketing pages.
    const path = location.pathname || "/";
    const isProScope = path === "/pro" || path.startsWith("/pro/");
    if (!isProScope) return;
    // Only run for authed users.
    const token = localStorage.getItem("cauldron_token");
    if (!token) return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    const API = process.env.REACT_APP_BACKEND_URL + "/api";
    fetch(`${API}/tiers/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.features?.crm_notes) {
          setVisible(true);
          track("view", 0);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const dismiss = (reason = "close") => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    track(reason);
    setVisible(false);
  };

  const next = () => {
    track("next");
    setStep(s => {
      const n = s + 1;
      // Fire a view event for the new step.
      setTimeout(() => track("view", n), 0);
      return n;
    });
  };

  if (!visible) return null;
  const S = STEPS[step];
  const Icon = S.icon;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="onboarding-tour">
      <div className="relative bg-gradient-to-br from-neutral-900 to-neutral-950 border border-amber-500/40 rounded-xl w-full max-w-md m-4 p-8 shadow-2xl">
        <button
          onClick={() => dismiss("close")}
          className="absolute top-3 right-3 text-amber-200/40 hover:text-amber-100"
          data-testid="tour-close"
          aria-label="Close"
        ><X className="h-5 w-5" /></button>

        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-full bg-amber-950/60 border border-amber-500/30 text-amber-400">
            <Icon className="h-6 w-6" />
          </div>
          <div className="text-xs uppercase tracking-widest text-amber-300/60">
            Pro tour · {step + 1} of {STEPS.length}
          </div>
        </div>

        <h2 className="text-2xl font-serif text-amber-50 mb-3" data-testid="tour-title">{S.title}</h2>
        <p className="text-amber-100/80 leading-relaxed mb-6">{S.body}</p>

        {/* Progress bar */}
        <div className="flex gap-1 mb-6">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded ${i <= step ? "bg-amber-500" : "bg-neutral-700"}`}
              data-testid={`tour-progress-${i}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => dismiss("skip")}
            className="text-sm text-amber-200/60 hover:text-amber-100"
            data-testid="tour-skip"
          >Skip tour</button>
          <div className="flex gap-2">
            <Link
              to={S.to}
              onClick={() => { track("cta"); dismiss("cta"); }}
              className="px-3 py-2 text-sm rounded-md border border-amber-500/40 text-amber-100 hover:bg-amber-950/40"
              data-testid="tour-cta"
            >{S.cta}</Link>
            {step < STEPS.length - 1 ? (
              <button
                onClick={next}
                className="px-4 py-2 text-sm rounded-md bg-amber-500 hover:bg-amber-400 text-neutral-900 font-medium"
                data-testid="tour-next"
              >Next</button>
            ) : (
              <button
                onClick={() => dismiss("done")}
                className="px-4 py-2 text-sm rounded-md bg-amber-500 hover:bg-amber-400 text-neutral-900 font-medium"
                data-testid="tour-done"
              >Get started</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
